import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api.js';
import type { AgentTurn, ProjectRole, ProposalSummary } from '@integrate/contracts';
import {
  VoiceCapture,
  VoiceUnavailableError,
  browserCapture,
  pickContainer,
  type CaptureEnvironment,
} from '../voice/capture.js';
import type { SeparationState } from '@integrate/core';
import { AssistantThread } from './AssistantThread.js';
import { AssistantVoiceStatus, useVoiceConversation } from './AssistantVoice.js';
import type { RealtimeAudioEnvironment } from '../voice/realtime-audio.js';
import type { OpenVoiceSocket } from '../voice/realtime-session.js';

/**
 * The conversation and the box a sentence goes into.
 *
 * P6l built the route and left it reachable by `curl` only — the fifth appearance of the shape the P6
 * exit review measured four times, and the reason this exists.
 *
 * IT WAS A MODAL AND IS NOT ONE NOW, which is the whole of P10 in one sentence. `AssistantModal` opened
 * over the chart, held a SINGLE answer, and threw it away at the top of the next `ask` — so "how many
 * people are in Finance?" followed by "move the ones above 60%" could not work, because by the second
 * sentence nothing on either side of the wire remembered the first. The thread now lives on the server
 * (`agent_turns`) and this renders it.
 *
 * THREE OUTCOMES, AND ALL THREE ARE SHOWN — see `AssistantThread`, which owns that rendering now. A
 * client that showed only the proposal would be blank for a question answered in words and silently
 * lossy for a call that did not survive validation.
 *
 * WHAT THIS FILE STILL OWNS: the composer, dictation, and the one request in flight. The turns
 * themselves belong to the dock, because they outlive this panel — the microphone does not, and that
 * asymmetry is the reason for the split.
 */
export function AssistantPanel({
  projectId,
  scenarioId,
  turns,
  state,
  voiceAvailable,
  realtimeAvailable,
  mode,
  role,
  myUserId,
  onProposalSettled,
  captureEnvironment = browserCapture(),
  realtimeAudio,
  openVoiceSocket,
  onAsked,
  onOpenProposals,
}: {
  projectId: string;
  /** The scenario this request is being made IN. The model is never offered the field. */
  scenarioId: string | null;
  /** The conversation so far, oldest first, owned by the dock. */
  turns: readonly AgentTurn[];
  /** The design, so the rendered changes can name people rather than ids. Null while it loads. */
  state: SeparationState | null;
  /**
   * Whether the SERVER has a speech deployment. The browser's own answer is asked for separately below,
   * and both have to be yes: a microphone that can record into a service that is not configured is a
   * button whose only outcome is a refusal.
   */
  voiceAvailable: boolean;
  /**
   * Whether the server has a REALTIME deployment, which is a third answer and not a detail of the
   * second.
   *
   * Transcription and realtime are two Azure deployments that fail independently: a tenant can have
   * dictation and no spoken conversation, or the reverse. One flag standing for both would make one
   * absent feature look like two, and would put a Talk button on screen whose only outcome is a
   * refusal at the socket.
   */
  realtimeAvailable: boolean;
  /**
   * Which microphone the one button is, chosen by the toggle in the dock's head.
   *
   * TWO BUTTONS BECAME ONE, and the reason is that they were never a pair of things to do — they are
   * two ways of doing the same thing, and a row offering both asked the reader to re-decide on every
   * request. The mode is a setting; the button is an action.
   */
  mode: 'dictate' | 'talk';
  /** Whose decision buttons to draw under a proposal in the thread. The server still decides. */
  role: ProjectRole | null;
  myUserId: string | null;
  onProposalSettled(turnId: string, proposal: ProposalSummary): void;
  /**
   * The browser's media APIs, injected.
   *
   * `MediaRecorder` and `getUserMedia` do not exist in happy-dom and a microphone is not something a test
   * run can be given, so the alternative to this seam is a surface only a human clicking a button can
   * exercise — which is how the five unreachable-surface findings in this project's history got shipped.
   */
  captureEnvironment?: CaptureEnvironment;
  /** The realtime half of the same seam. `AudioContext` does not exist in happy-dom either. */
  realtimeAudio?: RealtimeAudioEnvironment;
  openVoiceSocket?: OpenVoiceSocket;
  /** A turn that landed. The dock appends it rather than re-fetching the thread it already has. */
  onAsked(turn: AgentTurn): void;
  /** Where a raised proposal is actually read, so the answer can send somebody there. */
  onOpenProposals(): void;
}): JSX.Element {
  /*
    THE SESSION IS HELD WHATEVER THE MODE IS, and it matters that this is not conditional. A hook
    cannot be called on some renders and not others, and the alternative — mounting the voice
    component only in `talk` — is what used to make the mode toggle end a live conversation. Ending it
    is still what happens, but by the deliberate line in `start`'s guard below rather than by React
    unmounting something mid-sentence.
  */
  const voice = useVoiceConversation({
    projectId,
    scenarioId,
    audio: realtimeAudio,
    openSocket: openVoiceSocket,
    onTurn: onAsked,
  });

  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [dictation, setDictation] = useState<'idle' | 'recording' | 'transcribing'>('idle');

  const ask = (): void => {
    if (!prompt.trim() || busy) return;
    setBusy(true);
    setError('');
    void api
      .askAssistant(projectId, { prompt: prompt.trim(), scenarioId })
      .then((turn) => {
        /*
          CLEARED ONLY ON SUCCESS, and that placement is deliberate. The sentence is now in the
          transcript above, so leaving it in the box would show it twice — but a failed ask writes
          nothing on the server either, and clearing the box on a failure would make somebody retype a
          request that a transient 503 ate. Retrying costs one keystroke because of where this line is.
        */
        setPrompt('');
        onAsked(turn);
      })
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'The assistant could not be reached.'),
      )
      .finally(() => setBusy(false));
  };

  /* ---------- dictation ---------- */

  /*
    THE BROWSER'S HALF OF THE ANSWER. `voiceAvailable` says the server has a speech deployment; this says
    this browser can produce a container that deployment reads. `pickContainer` returns null rather than a
    default precisely so this question has an answer — a default would mean recording in a format nobody
    accepts, discovering it after the upload, and telling somebody their two minutes of speech are gone.
  */
  const canRecord = useMemo(
    () => pickContainer(captureEnvironment.isTypeSupported) !== null,
    [captureEnvironment],
  );

  const captureRef = useRef<VoiceCapture | null>(null);
  /*
    A ref to the LATEST collect, not the one that existed when the recorder was built.

    `VoiceCapture` takes its options once, at construction, and `onAutoStop` fires minutes later when the
    ceiling ends a recording on its own. Closing over the first render's function would call a `setPrompt`
    from a component state that has moved on.
  */
  const collectRef = useRef<() => void>(() => {});

  const captureFor = (): VoiceCapture =>
    (captureRef.current ??= new VoiceCapture(captureEnvironment, {
      onAutoStop: () => collectRef.current(),
    }));

  /*
    THE MICROPHONE IS RELEASED WHEN THIS UNMOUNTS, whatever state it was in.

    Closing the dock mid-recording is the ordinary way out of a dictation somebody thought better of, and
    without this the stream stays live: the browser's recording indicator stays lit and the microphone
    stays hot after the feature is visibly over, which people reasonably read as the application listening
    to them. `cancel` also throws away what was already captured, which is the right thing to do with
    audio of somebody speaking.

    It is also why the dock UNMOUNTS this panel on close rather than hiding it. A hidden panel would keep
    the stream live, and the conversation it would have been preserving is on the server anyway.
  */
  useEffect(() => () => captureRef.current?.cancel(), []);

  const startVoice = (): void => {
    if (dictation !== 'idle') return;
    setError('');
    setNotice('');
    setDictation('recording');
    void captureFor()
      .start()
      .catch((cause: unknown) => {
        setDictation('idle');
        setError(cause instanceof Error ? cause.message : 'The microphone is not available.');
      });
  };

  const collectVoice = (): void => {
    if (dictation !== 'recording') return;
    setDictation('transcribing');
    void captureFor()
      .finish()
      .then(async (recording) => {
        if (recording.truncated) {
          setNotice('That is as much as could be recorded in one go — the rest was not captured.');
        }
        const { text } = await api.transcribe(projectId, recording);
        /*
          APPENDED, NEVER REPLACING. Somebody may have typed the start of a request and dictated the rest,
          and a transcript that overwrote it would destroy typed text to save a keystroke. It also lands
          in the box rather than being sent: a mis-transcribed instruction posted straight to a metered
          model is worse than one the person reads first, and reading it first is the whole reason this
          fills the textarea instead of calling `ask`.
        */
        setPrompt((current) => (current.trim() ? `${current.trim()} ${text}` : text));
      })
      .catch((cause: unknown) => {
        // A cancellation is somebody changing their mind, not a failure to report back to them.
        if (cause instanceof VoiceUnavailableError && cause.kind === 'cancelled') return;
        setError(cause instanceof Error ? cause.message : 'That recording could not be transcribed.');
      })
      .finally(() => setDictation('idle'));
  };
  collectRef.current = collectVoice;

  return (
    <>
      <div className="assistant-body">
        {turns.length === 0 ? (
          <p className="muted small" data-testid="assistant-empty">
            Describe the change you want. Nothing takes effect: whatever it suggests becomes a proposal
            somebody reads as a diff and applies under their own name.
          </p>
        ) : (
          <AssistantThread
            turns={turns}
            scenarioId={scenarioId}
            projectId={projectId}
            state={state}
            role={role}
            myUserId={myUserId}
            onProposalSettled={onProposalSettled}
            onOpenProposals={onOpenProposals}
          />
        )}

        {busy && (
          /*
            Beneath the transcript rather than over it. A spinner that covered the conversation would
            take away the one thing worth reading while waiting — which is usually the answer the
            question in flight refers back to.
          */
          <p className="muted small" data-testid="assistant-pending">
            Thinking…
          </p>
        )}
      </div>

      <div className="assistant-composer">
        {notice && (
          <p className="muted small" data-testid="assistant-voice-notice">
            {notice}
          </p>
        )}

        {error && (
          <p className="error" data-testid="assistant-error" role="alert">
            {error}
          </p>
        )}

        {/*
          ABOVE THE BOX, NOT BESIDE THE BUTTONS. A live conversation is the state the whole panel is
          in, not one more thing you can press, and it needs room the button row does not have — a
          state, a clock, a mute and a partial transcript. The CONTROL is in the row below, with Ask,
          because that is where everything you press lives.
        */}
        {realtimeAvailable && <AssistantVoiceStatus voice={voice} />}

        <textarea
          data-testid="assistant-prompt"
          rows={2}
          value={prompt}
          disabled={busy}
          placeholder="put everyone in Finance with dedication above 60% into the carve-out"
          onChange={(e) => setPrompt(e.target.value)}
          /*
            Ctrl/Cmd-Enter, not Enter. This is a multi-line box and a request is a sentence somebody
            may want to rewrite; binding the bare Return key would send half-formed instructions to a
            metered service every time somebody reached for a new line.
          */
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) ask();
          }}
        />

        <div className="sp-row">
          <button data-testid="assistant-ask" disabled={busy || !prompt.trim()} onClick={ask}>
            {busy ? 'Asking…' : 'Ask'}
          </button>

          {/*
            BOTH ANSWERS, or no button. The server must have a speech deployment and this browser must
            be able to record a container it reads; either alone puts a microphone on screen that can
            only disappoint. It never sends — dictating fills the box, and pressing Ask is still a
            separate, deliberate act.
          */}
          {/*
            ONE MICROPHONE BUTTON, and which one it is was decided at the top rather than here. They
            were never a pair of things to do — they are two ways of doing the same thing, and a row
            offering both asked the reader to re-decide on every request.
          */}
          {mode === 'dictate' && voiceAvailable && canRecord && (
            <button
              data-testid="assistant-dictate"
              className={dictation === 'recording' ? 'active' : ''}
              disabled={busy || dictation === 'transcribing'}
              aria-pressed={dictation === 'recording'}
              onClick={dictation === 'recording' ? collectVoice : startVoice}
              title="Dictate the request. It goes into the box for you to read — nothing is sent."
            >
              {dictation === 'recording'
                ? 'Stop'
                : dictation === 'transcribing'
                  ? 'Transcribing…'
                  : 'Dictate'}
            </button>
          )}

          {mode === 'talk' && realtimeAvailable && (
            <button
              data-testid="assistant-talk"
              className={voice.status === 'live' ? 'active' : ''}
              disabled={voice.status === 'opening'}
              aria-pressed={voice.status === 'live'}
              onClick={voice.status === 'live' ? voice.stop : voice.start}
              title={
                voice.status === 'live'
                  ? 'End the conversation and release the microphone.'
                  : 'Talk to the assistant. It answers out loud, and anything it decides to do becomes a proposal somebody reviews.'
              }
            >
              {voice.status === 'live' ? 'End' : voice.status === 'opening' ? 'Connecting…' : 'Talk'}
            </button>
          )}

        </div>
      </div>
    </>
  );
}
